import React, { useEffect, useMemo, useState } from "react";
import { eventBus } from "utils/eventBus";
import HeaderPortal from "components/portal/header-portal";
import List from "components/list";
import NavSlot from "@/components/navigation/tabs";
import { useNotificationData } from "@/modules/inbox/useNotificationsData";
import { useMarkReadMutation, useBulkMarkStatusMutation } from "./mutation";
import { invalidateAallNotificationQueries } from "./queries";
import CollectionFilters from "./filter";
import InboxToolbar from "./components/toolbar";
import { useInboxColumns } from "./hooks/useInboxColumns";
import { useInboxHref } from "./hooks/useInboxHref";
import {
  NOTIFICATION_ENDPOINT,
  NOTIFICATION_FIELDS,
  INBOX_NAV_ITEMS,
} from "./constants";

export default function Inbox({ status }) {
  const [notifications, setNotifications] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const dataMap = useNotificationData(notifications);

  // Reset selection when switching tabs
  useEffect(() => {
    setSelectedIds([]);
  }, [status]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    invalidateAallNotificationQueries();
    eventBus.emit("refresh:list", `${NOTIFICATION_ENDPOINT}${status}`);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const markReadMutation = useMarkReadMutation({
    onSuccess: () => {
      eventBus.emit("refresh:list", `${NOTIFICATION_ENDPOINT}${status}`);
    },
  });

  const bulkMutation = useBulkMarkStatusMutation({
    onSuccess: () => {
      setSelectedIds([]);
      eventBus.emit("refresh:list", `${NOTIFICATION_ENDPOINT}${status}`);
    },
  });

  const pageIds = useMemo(
    () => notifications.map((n) => n.id).filter(Boolean),
    [notifications]
  );

  const allSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const someSelected =
    pageIds.some((id) => selectedIds.includes(id)) && !allSelected;

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(Array.from(new Set([...selectedIds, ...pageIds])));
    } else {
      const pageIdSet = new Set(pageIds);
      setSelectedIds(selectedIds.filter((id) => !pageIdSet.has(id)));
    }
  };

  const toggleSelect = (id, checked) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((item) => item !== id));
    }
  };

  const handleBulkClear = () => {
    if (selectedIds.length === 0) return;
    bulkMutation.mutateAsync({ ids: selectedIds, status: "archived" });
  };

  const columns = useInboxColumns({
    status,
    allSelected,
    someSelected,
    toggleSelectAll,
    pageIds,
    selectedIds,
    toggleSelect,
    markReadMutation,
    bulkMutation,
  });

  const getHref = useInboxHref(dataMap);

  const baseFilters = {
    status: { _eq: status },
    recipient: { _eq: "$CURRENT_USER" },
  };

  return (
    <div className="w-full">
      <HeaderPortal>
        <NavSlot navItems={INBOX_NAV_ITEMS} />
      </HeaderPortal>

      <InboxToolbar
        status={status}
        allSelected={allSelected}
        someSelected={someSelected}
        toggleSelectAll={toggleSelectAll}
        pageIdsLength={pageIds.length}
        selectedCount={selectedIds.length}
        isRefreshing={isRefreshing}
        onRefresh={handleRefresh}
        onClearSelected={handleBulkClear}
        onDeselectAll={() => setSelectedIds([])}
        isMutating={bulkMutation.isPending}
      />

      <div className="flex-1 h-full" key={JSON.stringify(baseFilters)}>
        <List
          className="w-full"
          showHeader={false}
          showIndex={false}
          title="Notifications"
          endPoint={NOTIFICATION_ENDPOINT}
          sortBy="timestamp"
          sortOrder="desc"
          columns={columns}
          filters={baseFilters}
          showSearch={false}
          meta={{ fields: NOTIFICATION_FIELDS }}
          storageKey={status}
          rowClassName="h-16"
          href={getHref}
          onDataChange={setNotifications}
          renderFilters={CollectionFilters}
          teleportFilters={true}
          hideFilterButton={true}
          showListHeader={false}
        />
      </div>
    </div>
  );
}
